import { useState, useEffect, useMemo, useCallback } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bell, Lock, Trash2, Info, Clock, CalendarOff, Plus, X, MessageSquare, GitBranch, RotateCcw } from "lucide-react";
import { AGENDA_DAYS, DEFAULT_OPENING_HOURS, normalizeOpeningHours } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    notificationEmail: "docteur@exemple.fr",
    retentionDays: 90
  });

  // --- Horaires d'ouverture & absences (persistés côté serveur) ---
  const userEmail = useMemo(
    () => (JSON.parse(localStorage.getItem('fruitycall_user') || '{}').email || ""),
    []
  );
  const [openingHours, setOpeningHours] = useState(DEFAULT_OPENING_HOURS);
  const [absences, setAbsences] = useState([]);
  const [accepteNouveaux, setAccepteNouveaux] = useState(true);
  const [faq, setFaq] = useState([]);
  const [faqForm, setFaqForm] = useState({ mots_cles: "", reponse: "" });
  const [scenarios, setScenarios] = useState([]);
  const [actionsScenarios, setActionsScenarios] = useState([]);
  const [scenariosModifies, setScenariosModifies] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });

  const loadAgendaConfig = useCallback(async () => {
    if (!userEmail) return;
    try {
      const response = await fetch(`${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      setOpeningHours(normalizeOpeningHours(data.opening_hours));
      setAbsences(data.absences || []);
      setAccepteNouveaux(data.accepte_nouveaux_patients !== false);
      setFaq(data.faq || []);
      setScenarios(data.scenarios || []);
      setScenariosModifies(false);
    } catch (error) {
      console.error("Erreur chargement paramètres agenda:", error);
    }
  }, [userEmail]);

  useEffect(() => {
    loadAgendaConfig();
  }, [loadAgendaConfig]);

  useEffect(() => {
    fetch(`${API_URL}/api/voice/scenario-actions`)
      .then((r) => r.json())
      .then((d) => setActionsScenarios(d.actions || []))
      .catch(() => setActionsScenarios([]));
  }, []);

  const persistAgendaConfig = async (payload, successMessage) => {
    try {
      const response = await fetch(
        `${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error();
      toast.success(successMessage);
      return true;
    } catch {
      toast.error("Erreur lors de l'enregistrement");
      return false;
    }
  };

  const updateDay = (dayKey, patch) => {
    setOpeningHours((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], ...patch } }));
  };

  const updateRange = (dayKey, index, patch) => {
    setOpeningHours((prev) => {
      const ranges = prev[dayKey].ranges.map((r, i) => (i === index ? { ...r, ...patch } : r));
      return { ...prev, [dayKey]: { ...prev[dayKey], ranges } };
    });
  };

  const addRange = (dayKey) => {
    setOpeningHours((prev) => {
      const ranges = [...prev[dayKey].ranges, { start: "14:00", end: "17:00" }];
      return { ...prev, [dayKey]: { ...prev[dayKey], ranges } };
    });
  };

  const removeRange = (dayKey, index) => {
    setOpeningHours((prev) => {
      const ranges = prev[dayKey].ranges.filter((_, i) => i !== index);
      return {
        ...prev,
        [dayKey]: { ...prev[dayKey], ranges: ranges.length ? ranges : [{ start: "09:00", end: "17:00" }] },
      };
    });
  };

  const saveOpeningHours = () =>
    persistAgendaConfig({ opening_hours: openingHours }, "Horaires d'ouverture enregistrés");

  const addAbsence = async () => {
    if (!absenceForm.start_date || !absenceForm.end_date) {
      toast.error("Indiquez les dates de début et de fin");
      return;
    }
    if (absenceForm.end_date < absenceForm.start_date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    const { start_time, end_time } = absenceForm;
    if (!!start_time !== !!end_time) {
      toast.error("Indiquez l'heure de début et l'heure de fin, ou aucune pour la journée entière");
      return;
    }
    if (start_time && end_time <= start_time) {
      toast.error("L'heure de fin doit être après l'heure de début");
      return;
    }
    const entree = { id: Date.now().toString(), ...absenceForm };
    if (!start_time) {
      delete entree.start_time;
      delete entree.end_time;
    }
    const next = [...absences, entree].sort((a, b) =>
      (a.start_date + (a.start_time || "")).localeCompare(b.start_date + (b.start_time || ""))
    );
    if (await persistAgendaConfig({ absences: next }, "Absence enregistrée")) {
      setAbsences(next);
      setAbsenceForm({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
    }
  };

  const removeAbsence = async (id) => {
    const next = absences.filter((a) => a.id !== id);
    if (await persistAgendaConfig({ absences: next }, "Absence supprimée")) {
      setAbsences(next);
    }
  };

  const addFaq = async () => {
    if (!faqForm.mots_cles.trim() || !faqForm.reponse.trim()) {
      toast.error("Indiquez les mots-clés et la réponse");
      return;
    }
    const next = [...faq, { id: Date.now().toString(), ...faqForm }];
    if (await persistAgendaConfig({ faq: next }, "Réponse type enregistrée")) {
      setFaq(next);
      setFaqForm({ mots_cles: "", reponse: "" });
    }
  };

  const removeFaq = async (id) => {
    const next = faq.filter((f) => f.id !== id);
    if (await persistAgendaConfig({ faq: next }, "Réponse type supprimée")) {
      setFaq(next);
    }
  };

  const modifierScenario = (id, patch) => {
    setScenarios((prev) => prev.map((sc) => (sc.id === id ? { ...sc, ...patch } : sc)));
    setScenariosModifies(true);
  };

  const ajouterScenario = () => {
    setScenarios((prev) => [
      ...prev,
      { id: Date.now().toString(), titre: "", situation: "", exemples: "", action: "message", phrase: "", actif: true },
    ]);
    setScenariosModifies(true);
  };

  const supprimerScenario = (id) => {
    setScenarios((prev) => prev.filter((sc) => sc.id !== id));
    setScenariosModifies(true);
  };

  const enregistrerScenarios = async () => {
    const incomplet = scenarios.find((sc) => !sc.titre.trim() || !sc.situation.trim());
    if (incomplet) {
      toast.error("Chaque scénario doit avoir un titre et une situation");
      return;
    }
    if (await persistAgendaConfig({ scenarios }, "Scénarios enregistrés")) {
      setScenariosModifies(false);
    }
  };

  const retablirScenarios = async () => {
    // Une liste vide fait revenir le serveur aux scénarios par défaut
    if (await persistAgendaConfig({ scenarios: [] }, "Scénarios par défaut rétablis")) {
      loadAgendaConfig();
    }
  };

  const formatAbsenceDate = (iso) => {
    const [y, m, d] = (iso || "").split("-");
    return y ? `${d}/${m}/${y}` : iso;
  };
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSaveNotifications = () => {
    toast.success("Paramètres de notification mis à jour");
  };
  
  const handleChangePassword = (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    toast.success("Mot de passe modifié avec succès");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };
  
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Paramètres</h1>
            <p className="text-muted-foreground">
              Gérez les paramètres de votre compte et vos préférences
            </p>
          </div>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Prise de rendez-vous */}
            <Card className="border-2 lg:col-span-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle>Prise de rendez-vous par la secrétaire IA</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="accepte-nouveaux">Accepter les nouveaux patients</Label>
                    <p className="text-sm text-muted-foreground">
                      Désactivé : au téléphone, la secrétaire demande si l'appelant est déjà suivi
                      au cabinet. Si c'est un nouveau patient, elle décline poliment la prise de
                      rendez-vous et propose de noter un message. Effet immédiat sur les appels.
                    </p>
                  </div>
                  <Switch
                    id="accepte-nouveaux"
                    checked={accepteNouveaux}
                    onCheckedChange={async (checked) => {
                      setAccepteNouveaux(checked);
                      await persistAgendaConfig(
                        { accepte_nouveaux_patients: checked },
                        checked
                          ? "Prise de RDV ouverte à tous les patients"
                          : "Prise de RDV réservée aux patients déjà suivis"
                      );
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Scénarios */}
            <Card className="border-2 lg:col-span-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-primary" />
                  <CardTitle>Scénarios de la secrétaire IA</CardTitle>
                </div>
                <CardDescription>
                  Pour chaque situation, choisissez ce que doit faire la secrétaire. Elle identifie
                  d'abord la situation de l'appelant, puis applique <span className="font-medium">uniquement</span> l'action
                  correspondante. Décrivez la situation, et surtout donnez des exemples de phrases
                  telles que vos patients les disent : c'est ce qui aide le plus la secrétaire à
                  reconnaître la bonne situation. La phrase, facultative, est prononcée au moment
                  d'appliquer l'action. Les urgences sont toujours orientées vers le 15, même si ce
                  scénario est désactivé.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {scenarios.map((sc) => (
                  <div
                    key={sc.id}
                    className={`rounded-lg border p-3 space-y-2 ${sc.actif ? "" : "opacity-60"}`}
                  >
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={sc.actif !== false}
                        onCheckedChange={(v) => modifierScenario(sc.id, { actif: v })}
                        aria-label="Activer ce scénario"
                      />
                      <Input
                        value={sc.titre}
                        onChange={(e) => modifierScenario(sc.id, { titre: e.target.value })}
                        placeholder="Titre du scénario"
                        className="font-medium"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 shrink-0 p-0 text-destructive"
                        onClick={() => supprimerScenario(sc.id)}
                        aria-label="Supprimer ce scénario"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Situation</Label>
                      <Textarea
                        value={sc.situation}
                        onChange={(e) => modifierScenario(sc.id, { situation: e.target.value })}
                        rows={2}
                        placeholder="Le patient demande…"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Exemples de phrases de patients (une par ligne)
                      </Label>
                      <Textarea
                        value={sc.exemples || ""}
                        onChange={(e) => modifierScenario(sc.id, { exemples: e.target.value })}
                        rows={2}
                        placeholder={"Je voudrais…\nIl me faut…"}
                      />
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Action</Label>
                        <select
                          value={sc.action}
                          onChange={(e) => modifierScenario(sc.id, { action: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          {actionsScenarios.map((a) => (
                            <option key={a.cle} value={a.cle}>
                              {a.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Phrase à dire (facultatif)</Label>
                        <Input
                          value={sc.phrase || ""}
                          onChange={(e) => modifierScenario(sc.id, { phrase: e.target.value })}
                          placeholder="Le docteur ne délivre pas…"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={ajouterScenario} className="gap-1">
                    <Plus className="h-4 w-4" />
                    Ajouter un scénario
                  </Button>
                  <Button variant="ghost" onClick={retablirScenarios} className="gap-1 text-muted-foreground">
                    <RotateCcw className="h-4 w-4" />
                    Rétablir les scénarios par défaut
                  </Button>
                  <Button onClick={enregistrerScenarios} disabled={!scenariosModifies} className="ml-auto">
                    Enregistrer les scénarios
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Réponses types */}
            <Card className="border-2 lg:col-span-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <CardTitle>Réponses types</CardTitle>
                </div>
                <CardDescription>
                  Questions fréquentes auxquelles la secrétaire répond <span className="font-medium">instantanément</span>,
                  sans temps de réflexion. Indiquez les mots que le patient peut employer, séparés
                  par des virgules, et la réponse exacte à prononcer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {faq.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune réponse type enregistrée.</p>
                ) : (
                  <div className="space-y-2">
                    {faq.map((f) => (
                      <div key={f.id} className="flex items-start justify-between gap-3 rounded-lg border p-3">
                        <div className="min-w-0 text-sm">
                          <div className="flex flex-wrap gap-1">
                            {f.mots_cles.split(",").filter((m) => m.trim()).map((m, i) => (
                              <span key={i} className="rounded bg-primary/10 px-1.5 text-xs text-primary">
                                {m.trim()}
                              </span>
                            ))}
                          </div>
                          <p className="mt-1">{f.reponse}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0 text-destructive"
                          onClick={() => removeFaq(f.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2 rounded-lg border p-3">
                  <div>
                    <Label htmlFor="faq-mots">Mots-clés du patient</Label>
                    <Input
                      id="faq-mots"
                      value={faqForm.mots_cles}
                      onChange={(e) => setFaqForm({ ...faqForm, mots_cles: e.target.value })}
                      placeholder="tarif, prix, combien, consultation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="faq-reponse">Réponse prononcée</Label>
                    <Input
                      id="faq-reponse"
                      value={faqForm.reponse}
                      onChange={(e) => setFaqForm({ ...faqForm, reponse: e.target.value })}
                      placeholder="La consultation est à 30 euros, carte vitale acceptée."
                    />
                  </div>
                  <Button onClick={addFaq} className="w-full gap-1">
                    <Plus className="h-4 w-4" />
                    Ajouter la réponse type
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Horaires d'ouverture */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <CardTitle>Horaires d'ouverture du cabinet</CardTitle>
                </div>
                <CardDescription>
                  Les horaires réels du cabinet : la secrétaire IA s'en sert pour annoncer aux
                  appelants si le cabinet est ouvert et quand il rouvre. Ajoutez plusieurs plages
                  par jour pour une pause déjeuner (ex. 9h-12h et 14h-17h).
                  <span className="mt-2 block">
                    À ne pas confondre avec les <span className="font-medium">plages de
                    rendez-vous</span>, réglées sur l'agenda : celles-ci déterminent les créneaux
                    que la secrétaire propose réellement aux patients.
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {AGENDA_DAYS.map(({ key, label }) => {
                  const day = openingHours[key];
                  return (
                    <div key={key} className="flex items-start gap-3">
                      <label className="flex items-center gap-2 w-24 shrink-0 text-sm pt-2">
                        <Checkbox
                          checked={day.open}
                          onCheckedChange={(checked) => updateDay(key, { open: !!checked })}
                        />
                        {label}
                      </label>
                      <div className="flex-1 space-y-1.5">
                        {day.open ? (
                          day.ranges.map((range, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Input
                                type="time"
                                className="w-28"
                                value={range.start}
                                onChange={(e) => updateRange(key, i, { start: e.target.value })}
                              />
                              <span className="text-muted-foreground text-sm">à</span>
                              <Input
                                type="time"
                                className="w-28"
                                value={range.end}
                                onChange={(e) => updateRange(key, i, { end: e.target.value })}
                              />
                              {day.ranges.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive"
                                  onClick={() => removeRange(key, i)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              {i === day.ranges.length - 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 gap-1 px-2 text-muted-foreground"
                                  onClick={() => addRange(key)}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  plage
                                </Button>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground pt-2">Fermé</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button onClick={saveOpeningHours} className="w-full">
                  Enregistrer les horaires
                </Button>
              </CardContent>
            </Card>

            {/* Absences */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarOff className="h-5 w-5 text-primary" />
                  <CardTitle>Absences</CardTitle>
                </div>
                <CardDescription>
                  Congés, formations, fermetures exceptionnelles : ces périodes apparaissent
                  bloquées sur l'agenda.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {absences.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune absence programmée.</p>
                ) : (
                  <div className="space-y-2">
                    {absences.map((absence) => (
                      <div
                        key={absence.id}
                        className="flex items-center justify-between rounded-lg border p-2.5 text-sm"
                      >
                        <div>
                          <span className="font-medium">
                            {absence.start_date === absence.end_date
                              ? `Le ${formatAbsenceDate(absence.start_date)}`
                              : `Du ${formatAbsenceDate(absence.start_date)} au ${formatAbsenceDate(absence.end_date)}`}
                            {absence.start_time
                              ? `, de ${absence.start_time} à ${absence.end_time}`
                              : ", journée entière"}
                          </span>
                          {absence.reason && (
                            <span className="text-muted-foreground"> — {absence.reason}</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive"
                          onClick={() => removeAbsence(absence.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="rounded-lg border p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="absence-start">Du</Label>
                      <Input
                        id="absence-start"
                        type="date"
                        value={absenceForm.start_date}
                        onChange={(e) =>
                          setAbsenceForm({ ...absenceForm, start_date: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="absence-end">Au (inclus)</Label>
                      <Input
                        id="absence-end"
                        type="date"
                        value={absenceForm.end_date}
                        onChange={(e) =>
                          setAbsenceForm({ ...absenceForm, end_date: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="absence-debut">De (optionnel)</Label>
                      <Input
                        id="absence-debut"
                        type="time"
                        step="900"
                        value={absenceForm.start_time}
                        onChange={(e) => setAbsenceForm({ ...absenceForm, start_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="absence-fin">À (optionnel)</Label>
                      <Input
                        id="absence-fin"
                        type="time"
                        step="900"
                        value={absenceForm.end_time}
                        onChange={(e) => setAbsenceForm({ ...absenceForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Laissez les heures vides pour une absence sur la journée entière.
                  </p>
                  <div>
                    <Label htmlFor="absence-reason">Motif (optionnel)</Label>
                    <Input
                      id="absence-reason"
                      value={absenceForm.reason}
                      onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })}
                      placeholder="Congés, formation…"
                    />
                  </div>
                  <Button onClick={addAbsence} className="w-full gap-1">
                    <Plus className="h-4 w-4" />
                    Ajouter l'absence
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle>Notifications</CardTitle>
                </div>
                <CardDescription>
                  Configurez vos préférences de notification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-notifications">Notifications par email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevez un email pour chaque nouveau message
                    </p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={settings.emailNotifications}
                    onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                  />
                </div>
                
                {settings.emailNotifications && (
                  <div className="space-y-2">
                    <Label htmlFor="notification-email">Email de notification</Label>
                    <div className="flex gap-2">
                      <Input
                        id="notification-email"
                        type="email"
                        value={settings.notificationEmail}
                        onChange={(e) => handleSettingChange('notificationEmail', e.target.value)}
                        placeholder="votre@email.fr"
                      />
                    </div>
                  </div>
                )}
                
                <Button onClick={handleSaveNotifications} className="w-full">
                  Enregistrer les préférences
                </Button>
              </CardContent>
            </Card>
            
            {/* Password */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  <CardTitle>Mot de passe</CardTitle>
                </div>
                <CardDescription>
                  Modifiez votre mot de passe de connexion
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full">
                    Changer le mot de passe
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Data Retention */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  <CardTitle>Conservation des messages</CardTitle>
                </div>
                <CardDescription>
                  Informations sur la conservation de vos données
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-2xl font-bold text-primary">{settings.retentionDays}</span>
                    <span className="text-sm text-muted-foreground">jours</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Les messages vocaux et leurs transcriptions sont conservés pendant {settings.retentionDays} jours par défaut.
                  </p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Vous pouvez archiver ou supprimer manuellement vos messages à tout moment depuis votre boîte de réception.
                </p>
                
                <div className="rounded-lg border border-warning/20 bg-warning/5 p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Note :</span> La durée de conservation est définie pour respecter les recommandations légales en matière de données de santé.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Danger Zone */}
            <Card className="border-2 border-destructive/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <CardTitle className="text-destructive">Zone dangereuse</CardTitle>
                </div>
                <CardDescription>
                  Actions irréversibles sur votre compte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer tous les messages</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement tous vos messages et transcriptions. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive" 
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer tous les messages
                  </Button>
                </div>
                
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer mon compte</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement votre compte et toutes vos données. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive"
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer mon compte
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;